import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { 
  Printer, Play, Square, CheckCircle, XCircle, Search, 
  Plus, RefreshCw, Loader2, Copy, Download, Users, Music, Zap, Hash, Ticket, Settings, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';

import { API_URL } from '../utils/api';

const PaperBingoHostPage = () => {
  const { gameCode } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get club info from navigation state (if coming from ClubBingoAdminPage)
  const clubId = location.state?.clubId;
  const clubName = location.state?.clubName;
  
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verifyCode, setVerifyCode] = useState('');
  const [verifyResult, setVerifyResult] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(!gameCode);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [newGame, setNewGame] = useState({ 
    game_name: clubName ? `${clubName} Bingo` : '', 
    host_name: '', 
    dance_selection: 'popular',
    club_id: clubId || null
  });
  const [ticketCount, setTicketCount] = useState(50);
  const [printRange, setPrintRange] = useState({ start: 1, count: 50 });
  const [printData, setPrintData] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [showExcludeDialog, setShowExcludeDialog] = useState(false);
  const [excludedTickets, setExcludedTickets] = useState(new Set());
  const [excludeFromTicket, setExcludeFromTicket] = useState('');

  // Fetch game data
  const fetchGame = useCallback(async () => {
    if (!gameCode) return;
    
    try {
      const response = await fetch(`${API_URL}/api/bingo/paper/game/${gameCode}`);
      if (response.ok) {
        const data = await response.json();
        setGame(data);
        // Update excluded tickets from game data
        if (data.excluded_tickets) {
          setExcludedTickets(new Set(data.excluded_tickets));
        }
      } else {
        toast.error('Game not found');
        navigate('/bingo');
      }
    } catch (err) {
      console.error('Error fetching game:', err);
    } finally {
      setLoading(false);
    }
  }, [gameCode, navigate]);

  useEffect(() => {
    if (gameCode) {
      fetchGame();
      const interval = setInterval(fetchGame, 5000); // Poll every 5 seconds
      return () => clearInterval(interval);
    } else {
      setLoading(false);
    }
  }, [gameCode, fetchGame]);

  // Create new game
  const handleCreateGame = async () => {
    if (!newGame.game_name || !newGame.host_name) {
      toast.error('Please fill in all fields');
      return;
    }

    setProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/bingo/paper/create-game`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newGame)
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Game created! Code: ${data.game_code}`);
        navigate(`/bingo/paper/${data.game_code}`);
        setShowCreateDialog(false);
      } else {
        toast.error(data.detail || 'Failed to create game');
      }
    } catch (err) {
      toast.error('Error creating game');
    } finally {
      setProcessing(false);
    }
  };

  // Quick Start - Create game, generate tickets, and download PDF in one click
  const handleQuickStart = async (ticketsToGenerate = 50) => {
    if (!newGame.game_name || !newGame.host_name) {
      toast.error('Please fill in Game Name and Host Name');
      return;
    }

    setProcessing(true);
    try {
      // Step 1: Create game
      toast.info('Creating game...');
      const createResponse = await fetch(`${API_URL}/api/bingo/paper/create-game`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newGame)
      });

      const createData = await createResponse.json();
      
      if (!createResponse.ok) {
        toast.error(createData.detail || 'Failed to create game');
        return;
      }

      const newGameCode = createData.game_code;
      toast.success(`Game created! Code: ${newGameCode}`);

      // Step 2: Generate tickets
      toast.info(`Generating ${ticketsToGenerate} tickets...`);
      const generateResponse = await fetch(`${API_URL}/api/bingo/paper/game/${newGameCode}/generate-tickets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ticket_count: ticketsToGenerate })
      });

      const generateData = await generateResponse.json();
      
      if (!generateResponse.ok) {
        toast.error(generateData.detail || 'Failed to generate tickets');
        navigate(`/bingo/paper/${newGameCode}`);
        return;
      }

      toast.success(`Generated ${generateData.tickets_generated} tickets!`);

      // Step 3: Trigger PDF download
      toast.info('Downloading PDF...');
      const pdfUrl = `${API_URL}/api/bingo/paper/game/${newGameCode}/download-pdf?start=1&count=${ticketsToGenerate}`;
      window.open(pdfUrl, '_blank');

      // Navigate to host page
      toast.success('Quick Start complete! Your PDF is downloading.');
      navigate(`/bingo/paper/${newGameCode}`);
      setShowCreateDialog(false);

    } catch (err) {
      console.error('Quick Start error:', err);
      toast.error('Error during Quick Start');
    } finally {
      setProcessing(false);
    }
  };

  // Generate tickets
  const handleGenerateTickets = async () => {
    setProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/bingo/paper/game/${gameCode}/generate-tickets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ticket_count: ticketCount })
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Generated ${data.tickets_generated} tickets!`);
        setShowGenerateDialog(false);
        fetchGame();
      } else {
        toast.error(data.detail || 'Failed to generate tickets');
      }
    } catch (err) {
      toast.error('Error generating tickets');
    } finally {
      setProcessing(false);
    }
  };

  // Get print data
  const handlePreparePrint = async () => {
    setProcessing(true);
    try {
      const response = await fetch(
        `${API_URL}/api/bingo/paper/game/${gameCode}/tickets-pdf?start=${printRange.start}&count=${printRange.count}`
      );
      const data = await response.json();
      
      if (response.ok) {
        setPrintData(data);
      } else {
        toast.error(data.detail || 'Failed to get print data');
      }
    } catch (err) {
      toast.error('Error getting print data');
    } finally {
      setProcessing(false);
    }
  };

  // Start game
  const handleStartGame = async () => {
    if (!window.confirm('Once started, no more tickets can be generated. Continue?')) return;

    try {
      const response = await fetch(`${API_URL}/api/bingo/paper/game/${gameCode}/start`, {
        method: 'POST'
      });

      if (response.ok) {
        toast.success('Game started!');
        fetchGame();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to start game');
      }
    } catch (err) {
      toast.error('Error starting game');
    }
  };

  // Call dance
  const handleCallDance = async (danceName = null) => {
    try {
      const response = await fetch(`${API_URL}/api/bingo/paper/game/${gameCode}/call`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dance_name: danceName })
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success(`Called: ${data.dance}`);
        fetchGame();
      } else {
        toast.error(data.message || data.detail || 'Failed to call dance');
      }
    } catch (err) {
      toast.error('Error calling dance');
    }
  };

  // Verify ticket
  const handleVerifyTicket = async () => {
    if (!verifyCode.trim()) {
      toast.error('Please enter a ticket code');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/bingo/paper/game/${gameCode}/verify-ticket`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ticket_code: verifyCode })
      });

      const data = await response.json();
      setVerifyResult(data);
      
      if (data.is_winner) {
        toast.success('WINNER! 🎉');
      } else if (data.valid) {
        toast.info('Not a winner yet');
      } else {
        toast.error(data.error || 'Invalid ticket');
      }
    } catch (err) {
      toast.error('Error verifying ticket');
    }
  };

  // Record winner
  const handleRecordWinner = async () => {
    if (!verifyResult?.is_winner) return;

    try {
      const response = await fetch(`${API_URL}/api/bingo/paper/game/${gameCode}/record-winner`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ticket_code: verifyResult.ticket_code })
      });

      if (response.ok) {
        toast.success('Winner recorded!');
        setVerifyResult(null);
        setVerifyCode('');
        fetchGame();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to record winner');
      }
    } catch (err) {
      toast.error('Error recording winner');
    }
  };

  // Save excluded tickets to server
  const handleSaveExcludedTickets = async () => {
    try {
      const response = await fetch(`${API_URL}/api/bingo/paper/game/${gameCode}/exclude-tickets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ excluded_tickets: Array.from(excludedTickets) })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'Excluded tickets updated');
        setShowExcludeDialog(false);
        fetchGame();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to update excluded tickets');
      }
    } catch (err) {
      toast.error('Error updating excluded tickets');
    }
  };

  // Toggle a ticket's excluded status
  const toggleExcludeTicket = (ticketNum) => {
    setExcludedTickets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(ticketNum)) {
        newSet.delete(ticketNum);
      } else {
        newSet.add(ticketNum);
      }
      return newSet;
    });
  };

  // Exclude all tickets from a certain number onwards
  const excludeFromNumber = () => {
    const fromNum = parseInt(excludeFromTicket);
    if (isNaN(fromNum) || fromNum < 1) {
      toast.error('Please enter a valid ticket number');
      return;
    }
    const tickets = game?.tickets || [];
    const maxTicket = tickets.length;
    if (fromNum > maxTicket) {
      toast.error(`Ticket number must be ${maxTicket} or less`);
      return;
    }
    
    const newExcluded = new Set(excludedTickets);
    for (let i = fromNum; i <= maxTicket; i++) {
      newExcluded.add(i);
    }
    setExcludedTickets(newExcluded);
    toast.success(`Excluded tickets ${fromNum} to ${maxTicket}`);
    setExcludeFromTicket('');
  };

  // Clear all exclusions
  const clearExclusions = () => {
    setExcludedTickets(new Set());
  };

  // End game
  const handleEndGame = async () => {
    if (!window.confirm('Are you sure you want to end this game? This cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/bingo/paper/game/${gameCode}/end`, {
        method: 'POST'
      });

      if (response.ok) {
        toast.success('Game ended');
        fetchGame();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to end game');
      }
    } catch (err) {
      toast.error('Error ending game');
    }
  };

  // Print tickets component
  const PrintableTickets = ({ data }) => {
    if (!data) return null;

    // Group tickets into sets of 4 for 2x2 layout on A4 pages
    const ticketPages = [];
    for (let i = 0; i < data.tickets.length; i += 4) {
      ticketPages.push(data.tickets.slice(i, i + 4));
    }

    return (
      <div className="print-area" id="print-tickets">
        <style>{`
          @media print {
            .no-print { display: none !important; }
            .print-area { display: block !important; }
            .ticket-a4-page { 
              page-break-after: always;
              page-break-inside: avoid;
            }
            .ticket-a4-page:last-child { page-break-after: auto; }
            @page {
              size: A4;
              margin: 10mm;
            }
          }
          .ticket-a4-page {
            width: 210mm;
            min-height: 297mm;
            padding: 5mm;
            box-sizing: border-box;
            display: grid;
            grid-template-columns: 1fr 1fr;
            grid-template-rows: 1fr 1fr;
            gap: 5mm;
          }
          .ticket-card {
            border: 2px solid #333;
            padding: 3mm;
            display: flex;
            flex-direction: column;
            background: white;
            box-sizing: border-box;
            height: 100%;
          }
          .ticket-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 2mm;
            padding-bottom: 2mm;
            border-bottom: 1px solid #ccc;
          }
          .ticket-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 1px;
            border: 1px solid #333;
            flex: 1;
          }
          .ticket-cell {
            border: 1px solid #999;
            padding: 2px;
            text-align: center;
            font-size: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: white;
            min-height: 18px;
            line-height: 1.1;
          }
          .ticket-footer {
            margin-top: 2mm;
            text-align: center;
            font-size: 7px;
            color: #666;
          }
          .ticket-title {
            font-weight: bold;
            font-size: 11px;
            margin: 0;
          }
          .ticket-subtitle {
            font-size: 8px;
            color: #666;
            margin: 0;
          }
          .ticket-code {
            font-size: 14px;
            font-weight: bold;
            font-family: monospace;
            margin: 0;
          }
          .ticket-number {
            font-size: 8px;
            color: #666;
            margin: 0;
          }
        `}</style>
        
        {ticketPages.map((pageTickets, pageIdx) => (
          <div key={pageIdx} className="ticket-a4-page">
            {pageTickets.map((ticket) => (
              <div key={ticket.ticket_code} className="ticket-card">
                <div className="ticket-header">
                  <div>
                    <p className="ticket-title">{data.game_name}</p>
                    <p className="ticket-subtitle">Code: {data.game_code}</p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p className="ticket-code">{ticket.ticket_code}</p>
                    <p className="ticket-number">#{ticket.ticket_number}</p>
                  </div>
                </div>
                
                <div className="ticket-grid">
                  {ticket.card.map((row, rowIdx) => (
                    row.map((dance, colIdx) => (
                      <div key={`${rowIdx}-${colIdx}`} className="ticket-cell">
                        {dance}
                      </div>
                    ))
                  ))}
                </div>
                
                <p className="ticket-footer">
                  Mark off dances as called. First to complete a column wins!
                </p>
              </div>
            ))}
            {/* Fill empty slots if less than 4 tickets on this page */}
            {pageTickets.length < 4 && [...Array(4 - pageTickets.length)].map((_, i) => (
              <div key={`empty-${i}`} className="ticket-card" style={{ border: '1px dashed #ccc' }}></div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  // Show create dialog if no game code
  if (!gameCode) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
        <div className="max-w-md mx-auto pt-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Printer className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Paper Bingo</h1>
            <p className="text-slate-400">Print tickets for offline play with 90 dances</p>
          </div>
          
          {/* Game Type Switcher */}
          <div className="flex flex-wrap justify-center gap-2 mb-6">
            <Link to="/bingo/host" className="bg-slate-800 hover:bg-slate-700 text-slate-300 px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors">
              <Music className="w-4 h-4" />
              Line Dance
            </Link>
            <Link to="/bingo/uk90/host" className="bg-slate-800 hover:bg-slate-700 text-slate-300 px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors">
              <Hash className="w-4 h-4" />
              UK 90-Ball
            </Link>
            <div className="bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2">
              <Printer className="w-4 h-4" />
              Paper Tickets
            </div>
            <Link to="/raffle/host" className="bg-slate-800 hover:bg-slate-700 text-slate-300 px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors">
              <Ticket className="w-4 h-4" />
              Raffle
            </Link>
          </div>

          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <Printer className="w-5 h-5 text-emerald-500" />
                Create Paper Bingo Game
              </CardTitle>
              <CardDescription className="text-slate-400">
                Generate printable tickets with 90 dances (5×3 grid)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1 text-slate-300">Game Name</label>
                <Input
                  value={newGame.game_name}
                  onChange={(e) => setNewGame(prev => ({ ...prev, game_name: e.target.value }))}
                  placeholder="Friday Night Bingo"
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="game-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-slate-300">Host Name</label>
                <Input
                  value={newGame.host_name}
                  onChange={(e) => setNewGame(prev => ({ ...prev, host_name: e.target.value }))}
                  placeholder="Your name"
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="host-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-slate-300">Dance Selection</label>
                <select
                  value={newGame.dance_selection}
                  onChange={(e) => setNewGame(prev => ({ ...prev, dance_selection: e.target.value }))}
                  className="w-full bg-slate-800 border border-slate-700 text-white rounded-md p-2"
                  data-testid="dance-selection"
                >
                  <option value="popular">Popular Dances</option>
                  <option value="random">Random Selection</option>
                </select>
              </div>
              
              {/* Quick Start Section */}
              <div className="border-t border-slate-700 pt-4 mt-4">
                <p className="text-sm font-medium text-center mb-3 text-emerald-400">⚡ Quick Start Options</p>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    onClick={() => handleQuickStart(25)}
                    disabled={processing}
                    className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                    data-testid="quick-start-25"
                  >
                    {processing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Zap className="w-4 h-4 mr-1" />}
                    25 Tickets
                  </Button>
                  <Button
                    onClick={() => handleQuickStart(50)}
                    disabled={processing}
                    className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                    data-testid="quick-start-50"
                  >
                    {processing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Zap className="w-4 h-4 mr-1" />}
                    50 Tickets
                  </Button>
                  <Button
                    onClick={() => handleQuickStart(100)}
                    disabled={processing}
                    className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                    data-testid="quick-start-100"
                  >
                    {processing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Zap className="w-4 h-4 mr-1" />}
                    100 Tickets
                  </Button>
                  <Button
                    onClick={() => handleQuickStart(200)}
                    disabled={processing}
                    className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                    data-testid="quick-start-200"
                  >
                    {processing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Zap className="w-4 h-4 mr-1" />}
                    200 Tickets
                  </Button>
                </div>
                <p className="text-xs text-slate-500 text-center mt-2">
                  Creates game + generates tickets + downloads PDF instantly
                </p>
              </div>

              <div className="border-t border-slate-700 pt-4">
                <p className="text-xs text-slate-500 text-center mb-2">Or create manually:</p>
                <Button
                  onClick={handleCreateGame}
                  disabled={processing}
                  variant="outline"
                  className="w-full border-slate-600 text-slate-300 hover:bg-slate-800"
                  data-testid="create-game-btn"
                >
                  {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                  Create Game (Manual Setup)
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p>Game not found</p>
      </div>
    );
  }

  const calledDances = game.called_dances || [];
  const tickets = game.tickets || [];
  const winners = game.winners || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 p-4">
      <div className="max-w-6xl mx-auto space-y-4">
        {/* Header */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold">{game.game_name}</h1>
                <p className="text-gray-600">
                  Code: <span className="font-mono font-bold text-purple-600">{game.game_code}</span>
                  {' • '}Host: {game.host_name}
                  {' • '}Status: <span className={`font-medium ${
                    game.status === 'active' ? 'text-green-600' : 
                    game.status === 'completed' ? 'text-gray-600' : 'text-yellow-600'
                  }`}>{game.status.toUpperCase()}</span>
                </p>
              </div>
              <div className="flex gap-2 flex-wrap">
                {game.status === 'setup' && (
                  <>
                    <Button onClick={() => setShowGenerateDialog(true)} variant="outline">
                      <Plus className="w-4 h-4 mr-1" /> Generate Tickets
                    </Button>
                    <Button onClick={() => setShowPrintDialog(true)} variant="outline" disabled={tickets.length === 0}>
                      <Printer className="w-4 h-4 mr-1" /> Print Tickets
                    </Button>
                    <Button onClick={() => setShowExcludeDialog(true)} variant="outline" disabled={tickets.length === 0}>
                      <Settings className="w-4 h-4 mr-1" /> Exclude Unsold
                      {excludedTickets.size > 0 && (
                        <span className="ml-1 bg-orange-500 text-white text-xs px-1.5 py-0.5 rounded-full">{excludedTickets.size}</span>
                      )}
                    </Button>
                    <Button onClick={handleStartGame} className="bg-green-600 hover:bg-green-700" disabled={tickets.length === 0}>
                      <Play className="w-4 h-4 mr-1" /> Start Game
                    </Button>
                  </>
                )}
                {game.status === 'active' && (
                  <>
                    <Button onClick={() => setShowPrintDialog(true)} variant="outline">
                      <Printer className="w-4 h-4 mr-1" /> Print Tickets
                    </Button>
                    <Button 
                      onClick={handleEndGame} 
                      variant="outline" 
                      className="border-red-500 text-red-500 hover:bg-red-50"
                    >
                      <Square className="w-4 h-4 mr-1" /> End Game
                    </Button>
                  </>
                )}
                {game.status === 'completed' && (
                  <span className="text-gray-500 italic">Game ended</span>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-3 gap-4">
          {/* Left: Call controls and verify */}
          <div className="lg:col-span-1 space-y-4">
            {/* Stats */}
            <Card>
              <CardContent className="pt-4">
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <p className="text-3xl font-bold text-purple-600">{tickets.length}</p>
                    <p className="text-sm text-gray-500">Tickets</p>
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-green-600">{calledDances.length}</p>
                    <p className="text-sm text-gray-500">/ 90 Called</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Call Dance */}
            {game.status === 'active' && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Call Next Dance</CardTitle>
                </CardHeader>
                <CardContent>
                  <Button 
                    onClick={() => handleCallDance()} 
                    className="w-full bg-purple-600 hover:bg-purple-700"
                    disabled={calledDances.length >= 90}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Call Random Dance
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Verify Ticket */}
            {game.status === 'active' && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Search className="w-5 h-5" />
                    Verify Ticket Claim
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex gap-2">
                    <Input
                      value={verifyCode}
                      onChange={(e) => setVerifyCode(e.target.value.toUpperCase())}
                      placeholder="Enter ticket code"
                      className="font-mono"
                      maxLength={6}
                    />
                    <Button onClick={handleVerifyTicket}>Check</Button>
                  </div>

                  {verifyResult && (
                    <div className={`p-3 rounded-lg ${
                      verifyResult.is_winner ? 'bg-green-100 border-green-500' : 
                      verifyResult.valid ? 'bg-yellow-100 border-yellow-500' : 'bg-red-100 border-red-500'
                    } border`}>
                      {verifyResult.is_winner ? (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-green-700">
                            <CheckCircle className="w-5 h-5" />
                            <span className="font-bold">WINNER!</span>
                          </div>
                          <p className="text-sm">Ticket #{verifyResult.ticket_number}</p>
                          <p className="text-sm">Winning dances: {verifyResult.winning_dances?.join(', ')}</p>
                          <Button onClick={handleRecordWinner} size="sm" className="bg-green-600 hover:bg-green-700">
                            Record Winner
                          </Button>
                        </div>
                      ) : verifyResult.valid ? (
                        <div className="flex items-center gap-2 text-yellow-700">
                          <XCircle className="w-5 h-5" />
                          <span>Not a winner yet</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-red-700">
                          <XCircle className="w-5 h-5" />
                          <span>{verifyResult.error}</span>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Winners */}
            {winners.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Winners 🎉</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {winners.map((winner, idx) => (
                      <div key={idx} className="p-2 bg-green-50 rounded border border-green-200">
                        <p className="font-mono font-bold">{winner.ticket_code}</p>
                        <p className="text-sm text-gray-600">
                          After {winner.called_count} calls
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right: Called dances */}
          <div className="lg:col-span-2">
            <Card className="h-full">
              <CardHeader className="pb-2">
                <CardTitle>Called Dances ({calledDances.length}/90)</CardTitle>
              </CardHeader>
              <CardContent>
                {calledDances.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No dances called yet</p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                    {calledDances.map((dance, idx) => (
                      <div 
                        key={idx} 
                        className={`p-2 rounded text-center text-sm ${
                          idx === calledDances.length - 1 
                            ? 'bg-purple-600 text-white font-bold' 
                            : 'bg-gray-100'
                        }`}
                      >
                        <span className="text-xs text-gray-500 mr-1">#{idx + 1}</span>
                        {dance}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Generate Tickets Dialog */}
        <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Generate Paper Tickets</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <label className="block text-sm font-medium mb-2">Number of Tickets</label>
              <Input
                type="number"
                value={ticketCount}
                onChange={(e) => setTicketCount(parseInt(e.target.value) || 1)}
                min={1}
                max={500}
              />
              <p className="text-sm text-gray-500 mt-2">
                Current tickets: {tickets.length}. Each ticket has a unique combination of 15 dances.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowGenerateDialog(false)}>Cancel</Button>
              <Button onClick={handleGenerateTickets} disabled={processing}>
                {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Generate
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Print Dialog */}
        <Dialog open={showPrintDialog} onOpenChange={setShowPrintDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Print Tickets</DialogTitle>
            </DialogHeader>
            
            {!printData ? (
              <div className="py-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Start from ticket #</label>
                    <Input
                      type="number"
                      value={printRange.start}
                      onChange={(e) => setPrintRange(prev => ({ ...prev, start: parseInt(e.target.value) || 1 }))}
                      min={1}
                      max={tickets.length}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Number of tickets</label>
                    <Input
                      type="number"
                      value={printRange.count}
                      onChange={(e) => setPrintRange(prev => ({ ...prev, count: parseInt(e.target.value) || 1 }))}
                      min={1}
                      max={100}
                    />
                  </div>
                </div>
                <p className="text-sm text-gray-500">
                  Total available: {tickets.length} tickets
                </p>
                <div className="flex gap-2">
                  <Button 
                    onClick={() => {
                      const url = `${API_URL}/api/bingo/paper/game/${gameCode}/download-pdf?start=${printRange.start}&count=${printRange.count}`;
                      window.open(url, '_blank');
                    }} 
                    disabled={processing} 
                    className="flex-1 bg-purple-600 hover:bg-purple-700"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download PDF
                  </Button>
                  <Button onClick={handlePreparePrint} disabled={processing} variant="outline" className="flex-1">
                    {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Printer className="w-4 h-4 mr-2" />}
                    Preview & Print
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="no-print flex justify-between items-center">
                  <p className="text-sm text-gray-600">
                    Showing tickets {printData.start} to {printData.start + printData.count - 1}
                  </p>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setPrintData(null)}>
                      Back
                    </Button>
                    <Button onClick={() => window.print()}>
                      <Printer className="w-4 h-4 mr-2" /> Print
                    </Button>
                  </div>
                </div>
                <PrintableTickets data={printData} />
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Exclude Unsold Tickets Dialog */}
        <Dialog open={showExcludeDialog} onOpenChange={setShowExcludeDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-orange-500" />
                Exclude Unsold Tickets
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Mark tickets that weren't sold so they won't be counted as potential winners.
                You have <strong>{tickets.length}</strong> tickets generated.
              </p>
              
              {/* Quick exclude from number */}
              <div className="bg-orange-50 p-4 rounded-lg space-y-2">
                <label className="block text-sm font-medium text-orange-800">Quick: Exclude from ticket # onwards</label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    value={excludeFromTicket}
                    onChange={(e) => setExcludeFromTicket(e.target.value)}
                    placeholder="e.g., 39"
                    min={1}
                    max={tickets.length}
                    className="w-28"
                  />
                  <Button onClick={excludeFromNumber} variant="outline" className="border-orange-400 text-orange-700 hover:bg-orange-100">
                    Exclude {excludeFromTicket && `#${excludeFromTicket}-${tickets.length}`}
                  </Button>
                </div>
                <p className="text-xs text-orange-600">
                  Example: If you sold tickets 1-38, enter 39 to exclude 39-{tickets.length}
                </p>
              </div>

              {/* Summary */}
              <div className="flex items-center justify-between py-2 border-y">
                <span className="text-sm">
                  <strong className="text-green-600">{tickets.length - excludedTickets.size}</strong> tickets in play
                  {excludedTickets.size > 0 && (
                    <span className="text-orange-600 ml-2">
                      (<strong>{excludedTickets.size}</strong> excluded)
                    </span>
                  )}
                </span>
                {excludedTickets.size > 0 && (
                  <Button variant="ghost" size="sm" onClick={clearExclusions} className="text-red-600 hover:text-red-700">
                    <X className="w-4 h-4 mr-1" /> Clear All
                  </Button>
                )}
              </div>

              {/* Ticket grid for manual selection */}
              <div>
                <label className="block text-sm font-medium mb-2">Or click to toggle individual tickets:</label>
                <div className="max-h-48 overflow-y-auto border rounded-lg p-2 bg-gray-50">
                  <div className="grid grid-cols-10 gap-1">
                    {tickets.map((t, idx) => {
                      const num = t.ticket_number;
                      const isExcluded = excludedTickets.has(num);
                      return (
                        <button
                          key={num}
                          onClick={() => toggleExcludeTicket(num)}
                          className={`w-8 h-8 text-xs font-mono rounded transition-colors ${
                            isExcluded 
                              ? 'bg-orange-500 text-white' 
                              : 'bg-white border border-gray-300 hover:border-orange-400'
                          }`}
                          title={isExcluded ? `Ticket #${num} excluded` : `Click to exclude ticket #${num}`}
                        >
                          {num}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Orange = excluded (won't count as winner)
                </p>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowExcludeDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveExcludedTickets} className="bg-orange-600 hover:bg-orange-700">
                Save Exclusions
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default PaperBingoHostPage;
